import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { DeskTable } from '@/components/common/DeskTable'
import FilterBarApply from '@/components/common/FilterBarApply'
import FilterBarForm from '@/components/common/FilterBarForm'
import PageFrame from '@/components/common/PageFrame'
import { Button } from '@/components/ui/button'
import { Sheet } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { backofficeApi, type FundQueueItem } from '@/lib/api-client'
import { dash, last7Days } from '@/lib/format'
import { useDeskSort } from '@/hooks/useDeskSort'
import { useListPageSize } from '@/hooks/useListPageSize'
import { resolveOffsetPagination } from '@/lib/list-pagination'
import { FILTER_BAR } from '@/lib/ui-shell'
import { fundColumnsForWidth } from '@/lib/fund-columns'
import { FUND_TABLE_CLASS, fundColumns, FundDetailList } from '@/pages/FundsQueuePage'

type Range = { from: string; to: string }
function rangeFromSearch(sp: URLSearchParams): Range {
  const days = last7Days()
  return { from: sp.get('from') || days.from, to: sp.get('to') || days.to }
}

export function FirstDepositsPage() {
  const [sp, setSp] = useSearchParams()
  const initial = rangeFromSearch(sp)
  const [draft, setDraft] = useState<Range>(initial)
  const [applied, setApplied] = useState<Range>(initial)
  useEffect(() => {
    if (sp.get('from') && sp.get('to')) return
    const next = rangeFromSearch(sp)
    const cur = new URLSearchParams(sp)
    cur.set('from', next.from)
    cur.set('to', next.to)
    setSp(cur, { replace: true })
  }, [sp, setSp])
  const [open, setOpen] = useState<FundQueueItem | null>(null)
  const rowRef = useRef<HTMLElement | null>(null)
  const page = Number(sp.get('page') || '1')
  const pageSize = useListPageSize()
  const { sort, onSortChange, sortQuery } = useDeskSort(['created', 'amount', 'kind', 'status', 'ref', 'acct', 'method', 'confirmed', 'name'])

  const q = useQuery({
    queryKey: ['first-deposits', applied, page, pageSize, sortQuery],
    queryFn: () =>
      backofficeApi.fundsBook({
        first_only: true,
        confirmed_from: applied.from || undefined,
        confirmed_to: applied.to || undefined,
        page,
        page_size: pageSize,
        include_total: true,
        ...sortQuery,
      }),
    placeholderData: keepPreviousData,
    enabled: !(applied.from && applied.to && applied.from > applied.to),
  })
  const items = q.data?.items ?? []
  const paging = resolveOffsetPagination({
    page,
    pageSize,
    total: q.data?.total,
    rowCount: items.length,
    hasMore: Boolean(q.data?.has_more),
  })
  const badRange = Boolean(draft.from && draft.to && draft.from > draft.to)
  const dirty = JSON.stringify(draft) !== JSON.stringify(applied)
  const fallback = rangeFromSearch(new URLSearchParams())

  function apply(next: Range) {
    setDraft(next)
    setApplied(next)
    const cur = new URLSearchParams(sp)
    cur.set('from', next.from)
    cur.set('to', next.to)
    cur.set('page', '1')
    cur.set('page_size', String(pageSize))
    setSp(cur, { replace: true })
  }

  return (
    <PageFrame
      title="First Deposits"
      description="Each client's first approved deposit, newest first. Filter by the day it was confirmed."
    >
      <FilterBarForm
        className={FILTER_BAR}
        onSubmit={() => {
          if (badRange) return
          apply(draft)
        }}
      >
        <div className="flex max-lg:flex-col max-lg:gap-3 lg:flex-row lg:flex-wrap lg:items-start lg:gap-3">
          <Field label="Confirmed from" className="min-w-40">
            <Input type="date" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} />
          </Field>
          <Field label="Confirmed to" className="min-w-40" error={badRange ? 'Must be on or after the start date' : undefined}>
            <Input type="date" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} />
          </Field>
          <FilterBarApply
            isDirty={dirty && !badRange}
            canReset={dirty || applied.from !== fallback.from || applied.to !== fallback.to}
            onReset={() => apply(fallback)}
          />
        </div>
      </FilterBarForm>
      <DeskTable
        columns={fundColumns}
        tableClassName={FUND_TABLE_CLASS}
        visibleKeys={fundColumnsForWidth}
        rows={items}
        rowKey={(r) => r.id}
        selectedKey={open?.id}
        onRowClick={(row, el) => {
          rowRef.current = el
          setOpen(row)
        }}
        loading={q.isLoading}
        error={q.error}
        dimmed={q.isFetching && q.isPlaceholderData}
        emptyTitle="No first deposits in this period"
        emptyDescription="Try a wider date range."
        sort={sort}
        onSortChange={onSortChange}
        paging={{
          total: paging.totalItems,
          page: paging.safePage,
          pageSize,
          onPage: (p) => {
            const cur = new URLSearchParams(sp)
            cur.set('page', String(p))
            cur.set('page_size', String(pageSize))
            setSp(cur, { replace: true })
          },
          onPageSize: (size) => {
            const cur = new URLSearchParams(sp)
            cur.set('page', '1')
            cur.set('page_size', String(size))
            setSp(cur, { replace: true })
          },
        }}
      />
      <Sheet open={Boolean(open)} onClose={() => setOpen(null)} label="First deposit detail" returnFocusRef={rowRef}>
        {open ? (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">{dash(open.full_name)}</h2>
                <p className="font-mono text-[12.5px] text-muted-foreground">{dash(open.client_code)}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setOpen(null)}>
                Close
              </Button>
            </div>
            <FundDetailList row={open} />
          </div>
        ) : null}
      </Sheet>
    </PageFrame>
  )
}
